import * as React from "react";
import { Check, Copy } from "lucide-react";
import { truncateDigest } from "@/lib/format";
import { cn } from "@/lib/utils";

export interface DigestChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: string;
  label?: string;
  truncate?: boolean;
  prefixLength?: number;
  suffixLength?: number;
  copyable?: boolean;
}

/**
 * DigestChip displays digests and identifiers in monospace with safe truncation
 * and one-click copy to clipboard.
 */
export function DigestChip({
  value,
  label,
  truncate = true,
  prefixLength = 12,
  suffixLength = 4,
  copyable = true,
  className,
  ...props
}: DigestChipProps) {
  const [copied, setCopied] = React.useState(false);

  const displayValue = truncate ? truncateDigest(value, prefixLength, suffixLength) : value;

  const copyAriaLabel = label ? `Copy ${label}` : `Copy ${truncate ? displayValue : value}`;

  async function handleCopy(e: React.MouseEvent) {
    e.stopPropagation();
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2 py-0.5 font-mono text-xs text-foreground",
        className,
      )}
      title={value}
      {...props}
    >
      <span className="select-all">{displayValue}</span>
      {copyable ? (
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? "Copied" : copyAriaLabel}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:size-4"
        >
          {copied ? (
            <Check aria-hidden className="size-3 text-success" />
          ) : (
            <Copy aria-hidden className="size-3" />
          )}
        </button>
      ) : null}
    </span>
  );
}
